import { ColDef, ValueGetterParams } from '@ag-grid-community/core';
import { ActiveStatusEnum } from 'app/model/entities/entity-model';
import { AgFns } from './ag-fns';
import { AgButtonProps } from './button-renderer.component';

export class UniAgFns {
  static getEntityDeleteColDef<T>(deleteMethodBound: (arg: T) => void | Promise<void> | Promise<boolean>, buttonProps?: AgButtonProps) {
    return {
      ...AgFns.createButtonProps('', deleteMethodBound, {
        icon: 'pi pi-trash',
        ...buttonProps,
      }),
      ...AgFns.fixWidth(60),
      suppressHeaderMenuButton: true,
    } as ColDef;
  }

  static getManufacturerColDef(field: string) {
    return {
      headerName: 'Manufacturer',
      field: field,
      filter: 'agTextColumnFilter',
      ...AgFns.flexWidth(150),
    } as ColDef;
  }

  static getProductTypeDescriptionColDef(field: string) {
    return {
      headerName: 'Description',
      field: field,
      filter: 'agTextColumnFilter',
      ...AgFns.flexWidth(150),
    } as ColDef;
  }

  static getProductTypeSKUColDef(field: string) {
    return {
      // deliberately removed because this column is often used for manual sorting
      // on a server side grid - and the colId is not known there.
      // colId: 'sku',
      headerName: 'SKU',
      field: field,
      filter: 'agTextColumnFilter',
      ...AgFns.flexWidth(90),
    } as ColDef;
  }

  static getFeatureChoicesColDef(field: string) {
    return {
      headerName: 'Features',
      field: field,
      // filter: "agTextColumnFilter", - deliberately no filter
      sortable: false,
      ...AgFns.flexWidth(100),
    } as ColDef;
  }

  static getActiveStatusColDef(field: string) {
    // Derive the sibling activeStatusId path from the display-name path so that
    // 'productType.activeStatus.name' -> 'productType.activeStatusId' and
    // 'activeStatus.name' -> 'activeStatusId'. Callers that pass a field NOT ending
    // in '.activeStatus.name' or literally 'activeStatus.name' get no color styling —
    // just the plain Status text.
    const statusIdPath = field.replace(/(^|\.)activeStatus\.name$/, '$1activeStatusId');
    return {
      headerName: 'Status',
      field: field,
      cellClass: (p) => {
        const statusId = UniAgFns.getNestedValue(p.data, statusIdPath);
        if (statusId === ActiveStatusEnum.Hold) return 'qb-status-hold';
        if (statusId === ActiveStatusEnum.Phaseout) return 'qb-status-phaseout';
        if (statusId === ActiveStatusEnum.Inactive) return 'qb-status-inactive';
        return '';
      },
      ...AgFns.fixWidth(140),
    } as ColDef;
  }

  /**
   * The Toggle Status button: Active <-> Phaseout. It never sets Inactive - that is chosen in the
   * edit dialog, which first checks for open work. Inactive and Hold go to Active.
   */
  static nextToggleStatus(statusId: ActiveStatusEnum) {
    return statusId === ActiveStatusEnum.Active ? ActiveStatusEnum.Phaseout : ActiveStatusEnum.Active;
  }

  /** Walk a dotted path on an object. Returns undefined at the first null hop. */
  private static getNestedValue(obj: any, path: string): any {
    if (obj == null || !path) return undefined;
    const parts = path.split('.');
    let cur: any = obj;
    for (const part of parts) {
      if (cur == null) return undefined;
      cur = cur[part];
    }
    return cur;
  }

  static getProductSortOrderColDef(productFn: (p: ValueGetterParams) => any, colId = 'productSortOrder', shouldHide = true) {
    return {
      colId: colId,
      headerName: 'Sort Order', // if not hidden
      hide: shouldHide,
      ...AgFns.fixWidth(120),
      valueGetter: (params) => {
        const product = productFn(params);
        return product?.getSortOrder();
      },
    };
  }
}
